import { Link } from "react-router-dom";
import { ArrowRight, Users, Clock, Check } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal, Stagger, StaggerItem } from "@/components/Motion";
import { Marquee } from "@/components/Marquee";
import { Seo } from "@/components/Seo";
import { GalleryCarousel } from "@/components/GalleryCarousel";
import { useContent } from "@/lib/api";

const goodToKnow = [
  { name: "Setup & cleanup included", detail: "30 minutes before and after every booking; extra time can be purchased", cap: "Included" },
  { name: "Decor is yours", detail: "Bring your own, or hire a decorator — the room is a blank canvas", cap: "Your vision" },
  { name: "Charcuterie add-on", detail: "Boards and grazing tables, quoted per event with your date", cap: "Optional" },
];

export default function Space() {
  const { data } = useContent();
  const s = data?.settings || {};
  const c = data?.content || {};
  const gallery = c.gallery || [];

  return (
    <div className="bg-bone">
      <Seo title="The Space — The Owl's Nest" description="Browse the dining room, lounge and gallery of The Owl's Nest, an intimate event venue for up to 100 guests." />
      <Nav />
      <section className="mx-auto max-w-7xl px-4 pt-32 sm:px-8 sm:pt-40 lg:px-16">
        <Reveal>
          <p className="overline-label">The Space</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            One room, a hundred ways to gather.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-stone-600 sm:text-lg sm:font-light">
            {c.description}
          </p>
        </Reveal>
        <Stagger className="mt-10 flex flex-wrap gap-3">
          <StaggerItem className="flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-sm font-medium text-stone-700">
            <Users className="h-4 w-4 text-gold" /> Up to {s.capacity || 100} guests
          </StaggerItem>
          {s.show_pricing && s.hourly_rate && (
            <StaggerItem className="flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-sm font-medium text-stone-700">
              <Clock className="h-4 w-4 text-gold" /> ${s.hourly_rate}/hour · {s.min_duration_hours}-hour minimum
            </StaggerItem>
          )}
        </Stagger>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-20 lg:px-16" data-testid="space-gallery">
        <Reveal className="mb-8">
          <p className="overline-label">Real events, real nights</p>
          <h2 className="mt-3 max-w-xl font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl">
            Flip through the celebrations.
          </h2>
        </Reveal>
        <GalleryCarousel items={gallery} />
      </section>

      <div className="mt-4"><Marquee /></div>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="overline-label">What's included</p>
            <h2 className="mt-4 font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl">
              The space, the tables, the chairs — the rest is yours.
            </h2>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-stone-600">
              We keep it simple: you rent the room, and every bit of decor comes from you or your
              hired decorator. Charcuterie is the one thing you can add from us.
            </p>
            <ul className="mt-10 grid gap-x-8 gap-y-4 sm:grid-cols-2">
              {(c.amenities || []).map((a) => (
                <li key={a} className="flex items-start gap-3 text-sm leading-relaxed text-stone-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {a}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="card bg-bone2 p-8 sm:p-10">
              <p className="overline-label">Good to know</p>
              <div className="mt-6 space-y-6">
                {goodToKnow.map((l) => (
                  <div key={l.name} className="flex items-start justify-between gap-4 border-b border-black/5 pb-6 last:border-0 last:pb-0">
                    <div>
                      <p className="font-serif text-xl text-ink">{l.name}</p>
                      <p className="mt-1 text-sm text-stone-600">{l.detail}</p>
                    </div>
                    <span className="whitespace-nowrap rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-stone-700">{l.cap}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal className="mt-16 flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-ink p-8 sm:p-12">
          <div>
            <p className="font-serif text-2xl text-bone sm:text-3xl">Picture your people here?</p>
            <p className="mt-2 text-sm text-stone-400">Check a date, then send an inquiry — it takes two minutes.</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/availability" data-testid="space-check-availability-btn" className="btn-dark !bg-bone !text-ink hover:!bg-white">
              Check Availability
            </Link>
            <Link to="/inquire" className="btn-ghost !border-bone/25 !text-bone hover:!border-bone/60">
              Inquire now <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>
      <Footer />
    </div>
  );
}
